import type {Component, Editor} from 'grapesjs';
import {isUserLoggedIn} from '@Libraries/user.library';
import {GA4EventName} from '@Libraries/ga-events';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {openAuthenticateModal} from '@Modals/authenticate-modal/authenticate-modal-library';
import {openErrorModal} from '@Modals/error-modal';
import {addUserTemplate} from '@Components/email-maker-block-manager/email-maker-block-manager-reducer';
import type {UserEmailComponentTemplate} from '@Components/email-maker-block-manager/email-maker-block-manager.types';
import {EMAIL_MAKER_COMPONENT_TYPES} from '@EmailMakerReact/email-maker-react.constants';
import {getInlineStyledComponentHtml} from '@EmailMakerReact/lib/email-maker-html-export-library';
import {getMediaQueries} from '@EmailMakerReact/lib/email-maker-save-payload-library';
import {logEmailMakerError} from '@EmailMakerReact/lib/email-maker-error-library';
import {AuthContext} from '@Libraries/login-signup-library';

const SAVE_SECTION_SIGNUP_SOURCE = 'email-maker-save-section';
const SAVE_SECTION_LOADING_KEY = 'email-maker-save-section-template';

const SAVE_USER_TEMPLATE_ENDPOINT = 'emailcomponenttemplate/saveUserTemplate';
const CREATE_USER_TEMPLATE_PREVIEW_ENDPOINT = 'emailcomponenttemplate/createUserTemplatePreview';

/** The section markup the admin save-section-template panel needs. */
export interface SectionTemplateContent {
  sectionHtmlContent: string;
  sectionJsonContent: string;
  htmlMediaQueries: string;
}

export interface SaveUserComponentTemplateOptions {
  /** Falls back to the current selection when omitted. */
  component?: Component;
  /** Runs after the save settles, whether it succeeded or failed. */
  callback: VoidFunction;
}

/**
 * Returns inline-styled HTML, component JSON, and media queries for a section, or null when the
 * target is not a saveable section.
 */
export function getSectionTemplateContent(editor: Editor, component?: Component): SectionTemplateContent | null {
  const section = component ?? editor.getSelected();

  if (!section || !shouldAllowSavingSectionTemplate(editor, section)) {
    return null;
  }

  return {
    sectionHtmlContent: getInlineStyledComponentHtml(editor, section),
    sectionJsonContent: JSON.stringify(section.toJSON()),
    htmlMediaQueries: getMediaQueries(editor),
  };
}

/** Saves a section as a reusable user block (behind the auth gate), then kicks off its preview render. */
export function saveUserComponentTemplate(editor: Editor, options: SaveUserComponentTemplateOptions): void {
  if (!isUserLoggedIn()) {
    openAuthenticateModal({
      signupSource: SAVE_SECTION_SIGNUP_SOURCE,
      authContext: AuthContext.EMAIL_MAKER,
      onLoginSuccess: (): Promise<void> => {
        showLoading(SAVE_SECTION_LOADING_KEY, {text: window.i18next.t('pmwjs_saving')});
        saveUserComponentTemplate(editor, {
          ...options,
          callback: (): void => {
            hideLoading(SAVE_SECTION_LOADING_KEY);
            options.callback();
          },
        });
        return Promise.resolve();
      },
    });

    return;
  }

  const content = getSectionTemplateContent(editor, options.component);
  if (!content) {
    // The callback contract covers every settle path; the post-login re-entry's wrapped callback also hides its keyed Saving toast here.
    options.callback();
    return;
  }

  void window.PMW.writeLocal(SAVE_USER_TEMPLATE_ENDPOINT, {
    htmlContent: content.sectionHtmlContent,
    jsonContent: content.sectionJsonContent,
    mediaQueries: content.htmlMediaQueries,
    name: window.i18next.t('pmwjs_section'),
  })
    .then((response: UserEmailComponentTemplate) => {
      window.PMW.redux.store.dispatch(addUserTemplate(response));
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_SECTION_SAVE);
      void window.PMW.writeLocal(CREATE_USER_TEMPLATE_PREVIEW_ENDPOINT, {
        templateId: response.id,
        htmlContent: content.sectionHtmlContent,
        jsonContent: response.jsonContent,
        mediaQueries: content.htmlMediaQueries,
      }).catch((e: unknown) => {
        logEmailMakerError('saveUserComponentTemplate: preview creation failed', e);
      });
    })
    .catch((e: unknown) => {
      logEmailMakerError('saveUserComponentTemplate: save failed', e);
      openErrorModal({message: window.i18next.t('pmwjs_unable_to_save')});
    })
    .finally(() => {
      options.callback();
    });
}

/** Only sections inside an existing wrapper may be saved as a template. */
function shouldAllowSavingSectionTemplate(editor: Editor, component: Component): boolean {
  if (!component.is(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_SECTION)) {
    return false;
  }

  return Boolean(editor.DomComponents.getWrapper());
}
